import { useState } from 'react'
import {
  Button, Card, Checkbox, Group, List, NumberInput, Select, Stack, Text, TextInput, Textarea,
} from '@mantine/core'
import type { AcaoDeWaf, Divergencia, ModoCodigo } from '@/api/waf'
import { TIPOS, gruposDoConjunto } from './acoesDoModulo'
import { ModalEmergencia } from './ModalEmergencia'

type Campos = ModoCodigo['campos']

function valorDe(v: unknown): string {
  return typeof v === 'string' ? v : JSON.stringify(v)
}

/** Monta o gesto a partir do formulário; `null` enquanto falta campo. */
function montar(tipo: string, grupo: string | null, subRegra: string, texto: string,
  numero: number | string, acao: string | null): AcaoDeWaf | null {
  switch (tipo) {
    case 'grupo_block': case 'grupo_count':
      return grupo ? { tipo, grupo } : null
    case 'excecao': case 'tirar_excecao':
      return grupo && /^[A-Za-z0-9_]{1,128}$/.test(subRegra) ? { tipo, grupo, sub_regra: subRegra } : null
    case 'geo_acao': case 'rate_acao':
      return acao ? { tipo, valor: acao } : null
    case 'geo_paises': {
      const paises = texto.split(/[\s,]+/).map(p => p.trim().toUpperCase()).filter(Boolean)
      return paises.length && paises.every(p => /^[A-Z]{2}$/.test(p)) ? { tipo, valor: paises } : null
    }
    case 'rate_limite':
      return typeof numero === 'number' && numero >= 100 ? { tipo, valor: numero } : null
    case 'liberar_ip': case 'tirar_ip':
      return texto.includes('/') ? { tipo, valor: texto.trim() } : null
    case 'adotar_aws':
      return { tipo }
    default:
      return null
  }
}

/**
 * Os gestos que o módulo waf expressa, pedidos ao código do stage (spec
 * 2026-10-14 D4/D6). Quem pede vê antes o que o apply leva junto: com
 * divergência, o botão só libera depois de marcar a caixa.
 */
export function ControlesDoModulo({
  campos, divergencias, ocupadoPor, enviando, erro, onEnviar,
  stage = '', emergencia,
}: {
  campos: Campos
  divergencias: Divergencia[]
  ocupadoPor: number | null
  enviando: boolean
  erro: string | null
  onEnviar: (body: { acao: AcaoDeWaf; justificativa: string; divergencias_aceitas: Divergencia[] }) => void
  stage?: string
  /** Só com `waf:emergencia` (spec D8); ausente, o botão não aparece. */
  emergencia?: {
    enviando: boolean
    erro: string | null
    aberta: boolean
    onAbrir: () => void
    onFechar: () => void
    onAplicar: (body: { acao: AcaoDeWaf; justificativa: string }) => void
  }
}) {
  const [tipo, setTipo] = useState<string | null>(null)
  const [grupo, setGrupo] = useState<string | null>(null)
  const [subRegra, setSubRegra] = useState('')
  const [texto, setTexto] = useState('')
  const [numero, setNumero] = useState<number | string>('')
  const [acao, setAcao] = useState<string | null>(null)
  const [justificativa, setJustificativa] = useState('')
  const [aceito, setAceito] = useState(false)

  const semExcecao = campos.managed_rule_overrides?.origem === 'indisponivel'
  const grupos = gruposDoConjunto(campos)
  const porExpressao = Object.entries(campos).filter(([, c]) => c.origem === 'expressao' || c.expressao)
  const comExpressao = new Set(porExpressao.map(([nome]) => nome))
  const opcoes = TIPOS
    .filter(t => !(semExcecao && t.campo === 'managed_rule_overrides'))
    .map(t => ({ value: t.tipo, label: t.rotulo, disabled: comExpressao.has(t.campo) }))

  const gesto = tipo ? montar(tipo, grupo, subRegra, texto, numero, acao) : null
  // "Trazer o código para o estado da AWS" não leva a AWS a lugar nenhum: o
  // código passa a ser o que ela já tem, então não há o que aceitar. A lista
  // vai do mesmo jeito: o worker a compara com a divergência na hora do plan
  // e recusa se o WAF tiver mudado depois de visto (`_motivo_livre`).
  const adotando = tipo === 'adotar_aws'
  // Stage ocupado não trava (spec 2026-10-15 §5.2): o pedido entra na fila.
  const pronto = !!gesto && justificativa.trim().length >= 10
    && (divergencias.length === 0 || adotando || aceito)
  // A emergência não espera o stage nem a lista de divergências: quem decide
  // cancelar ou esperar o pedido ativo é o servidor (D8, passo 2).
  const emergenciaPronta = !!gesto && gesto.tipo !== 'adotar_aws'

  return (
    <Card withBorder radius="sm" p="md">
      <Stack gap="sm">
        <Text fw={700} size="sm">Pedir mudança no código do stage</Text>
        {ocupadoPor != null && (
          <Text size="sm" c="dimmed">
            O stage está ocupado pelo pedido #{ocupadoPor}; o seu pedido entra na fila e roda depois dele.
          </Text>
        )}
        {porExpressao.map(([nome, c]) => (
          <Text key={nome} size="sm" c="dimmed">
            <Text span ff="monospace" size="sm">{nome}</Text>
            {' vem de '}
            <Text span ff="monospace" size="sm">{c.expressao ?? '(expressão)'}</Text>
            ; peça pelo chat.
          </Text>
        ))}
        <Select label="O que mudar" data={opcoes} value={tipo} onChange={setTipo} allowDeselect={false} />
        {tipo && ['grupo_block', 'grupo_count', 'excecao', 'tirar_excecao'].includes(tipo) && (
          <Select
            label="Grupo gerenciado"
            data={Object.entries(grupos).map(([value, label]) => ({ value, label }))}
            value={grupo}
            onChange={setGrupo}
          />
        )}
        {(tipo === 'excecao' || tipo === 'tirar_excecao') && (
          <TextInput label="Sub-regra" placeholder="SizeRestrictions_BODY" value={subRegra}
            onChange={e => setSubRegra(e.currentTarget.value)} styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }} />
        )}
        {(tipo === 'geo_acao' || tipo === 'rate_acao') && (
          <Select label="Ação" data={['count', 'block']} value={acao} onChange={setAcao} />
        )}
        {tipo === 'geo_paises' && (
          <TextInput label="Países (ISO, separados por vírgula)" placeholder="BR, US" value={texto}
            onChange={e => setTexto(e.currentTarget.value)} />
        )}
        {tipo === 'rate_limite' && (
          <NumberInput label="Limite" min={100} max={2_000_000_000} value={numero} onChange={setNumero} />
        )}
        {(tipo === 'liberar_ip' || tipo === 'tirar_ip') && (
          <TextInput label="IP em CIDR" placeholder="203.0.113.7/32" value={texto}
            onChange={e => setTexto(e.currentTarget.value)} styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }} />
        )}
        <Textarea label="Justificativa" description="Vai para o pedido e para o MR." minRows={2}
          value={justificativa} onChange={e => setJustificativa(e.currentTarget.value)} />
        {divergencias.length > 0 && adotando && (
          <Stack gap={4}>
            <Text size="sm">O código do stage passa a ser:</Text>
            <List size="sm" spacing={2}>
              {divergencias.filter(d => d.expressavel).map(d => (
                <List.Item key={d.campo}>
                  <Text span ff="monospace" size="sm">{d.campo}</Text>
                  {' = '}
                  <Text span ff="monospace" size="sm">{valorDe(d.na_aws)}</Text>
                  {' (como na AWS)'}
                </List.Item>
              ))}
            </List>
            {divergencias.some(d => !d.expressavel) && (
              <Text size="sm" c="dimmed">
                {'O módulo não guarda '}
                {divergencias.filter(d => !d.expressavel).map(d => d.campo).join(', ')}
                {'; nesses pontos o apply volta ao código.'}
              </Text>
            )}
          </Stack>
        )}
        {divergencias.length > 0 && !adotando && (
          <Stack gap={4}>
            <List size="sm" spacing={2}>
              {divergencias.map(d => (
                <List.Item key={d.campo}>
                  <Text span ff="monospace" size="sm">{d.campo}</Text>
                  {': AWS '}
                  <Text span ff="monospace" size="sm">{valorDe(d.na_aws)}</Text>
                  {' volta para '}
                  <Text span ff="monospace" size="sm">{valorDe(d.no_codigo)}</Text>
                </List.Item>
              ))}
            </List>
            <Checkbox
              checked={aceito}
              onChange={e => setAceito(e.currentTarget.checked)}
              label="O apply também leva a AWS de volta ao código nestes pontos"
            />
          </Stack>
        )}
        {erro && <Text size="sm" c="red">{erro}</Text>}
        <Group>
          <Button
            size="xs"
            disabled={!pronto}
            loading={enviando}
            onClick={() => gesto && onEnviar({
              acao: gesto, justificativa: justificativa.trim(),
              divergencias_aceitas: divergencias.length ? divergencias : [],
            })}
          >
            {ocupadoPor != null ? 'Entrar na fila' : 'Pedir ao código'}
          </Button>
          {emergencia && (
            <Button size="xs" color="red" variant="outline" disabled={!emergenciaPronta}
              onClick={emergencia.onAbrir}>
              Aplicar agora (emergência)
            </Button>
          )}
        </Group>
        {emergencia && (
          <ModalEmergencia
            opened={emergencia.aberta}
            acao={emergenciaPronta ? gesto : null}
            stage={stage}
            justificativaInicial={justificativa}
            enviando={emergencia.enviando}
            erro={emergencia.erro}
            onClose={emergencia.onFechar}
            onConfirmar={j => gesto && emergencia.onAplicar({ acao: gesto, justificativa: j })}
          />
        )}
      </Stack>
    </Card>
  )
}
